import { Box, Typography, CircularProgress, useTheme, alpha } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { SparkLineChart } from "./SparkLineChart";

interface MetricCardProps {
    title: string;
    value?: number;
    change: number;
    color: string;
    suffix?: string;
    isLoading?: boolean;
    data: any; //Change type later
    onSetTimePeriod: (period: string) => void;
    enableLabel: boolean;
}

export const MetricCard = ({ title, value, change, color, suffix = "", isLoading, data, onSetTimePeriod, enableLabel }: MetricCardProps) => {
    const theme = useTheme();

    return (
        <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: "0.625rem",
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: "0.15em",
                        color: theme.palette.text.secondary,
                    }}
                >
                    {title}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* Time period buttons - simplified for now */}

                    <Box
                        sx={{
                            display: "flex",
                            p: 0.5,
                            bgcolor: alpha(theme.palette.grey[100], 0.8),
                            borderRadius: 1,
                            gap: 0.5,
                        }}
                    ></Box>
                </Box>
            </Box>

            {/* Value and Change */}
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
                {isLoading ? (
                    <CircularProgress size={40} />
                ) : (
                    <>
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: "1rem", md: "1.5rem" },
                                fontWeight: 300,
                                color: theme.palette.text.primary,
                                letterSpacing: "0.1em",
                            }}
                        >
                            {(() => {
                                const dataset = data?.data?.datasets?.[0]?.data || data?.data?.chartData?.values || [];
                                // const lastValue = dataset.length > 0 ? dataset[dataset.length - 1] : 0;
                                // let finalValue = value !== undefined ? value : lastValue;

                                let workforceChange = data?.data?.datasets?.[0]?.data.reduce((a: number, b: number) => a + b, 0);
                                let workedHours = data?.data?.chartData?.values.reduce((a: number, b: number) => a + b, 0);

                                if (title === "Worked Hours") {
                                    workedHours = Number((workedHours / 60).toFixed(1));
                                    return `${workedHours} worked hours`;
                                }

                                return `${workforceChange} workforce additions`;
                            })()}

                            {suffix && (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: { xs: "1.5rem", md: "2rem" },
                                        opacity: 0.6,
                                        fontWeight: 600,
                                        ml: 0.5,
                                    }}
                                >
                                    {suffix}
                                </Typography>
                            )}
                        </Typography>
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1, justifyContent: "center", fontSize: ".8rem" }}>
                            {data?.data?.summary && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        // gap: 0.5,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 2,
                                        bgcolor: alpha(color, 0.1),
                                        width: "fit-content",
                                        mx: "auto",
                                        mb: "1rem",
                                    }}
                                >
                                    {/* <TrendingUpIcon sx={{ fontSize: 14, color }} /> */}
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color,
                                            }}
                                        >
                                            {" "}
                                            {title === "Workforce Change" ? data.data.summary.retentionRate + "%" : data.data.summary.newClientsGrowth + "%"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color: theme.palette.text.secondary,
                                                letterSpacing: "0.05em",
                                                marginLeft: ".6rem",
                                            }}
                                        >
                                            {title === "Workforce Change" ? "change in active workforce" : "more logged hours vs last period"}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Spark Line Chart */}
                            <Box sx={{ width: "100%", height: 150, position: "relative", mt: 2 }}>
                                <div style={{ position: "absolute", width: "100%", height: "100%" }}>
                                    <SparkLineChart data={data} title={title} enableLabel={enableLabel} />
                                </div>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
